//Component rendered on firing new App button on manage apps screen

// Library Utilities
import React, { useState } from "react";
import { ApiClientError } from "../../../../utils/apiResponse";
import PortalOverlay from "../../../../Shared/portal-overlay/portalOverlay";

// Hooks
import { useNavigate } from "react-router-dom";

// Functions
import { isValidName } from "../../../../utils/validation";
import validator from "validator";
import { callApi } from "../../../../api/fetch";

// Styles
import Styles from "../../../../Shared/popup.module.scss";
import {
  primaryButtonStyle,
  primaryButtonHoverStyle,
  cancelButtonHoverStyle,
  cancelButtonStyle,
} from "../../../../Shared/buttonStyles";

// Components
import Input from "../../../../Shared/input/input";
import ErrorMessage from "../../../../Shared/error-message/errorMessage";
import Button from "../../../../Shared/button/button";

// Constants
import { MEDIUM } from "../../../../Shared/buttonSize";

function CreateAppPopUp({ onCancel }: any) {
  const [appName, setAppName] = useState("");
  const [error, setError] = useState("");
  const [validAppName, setValidAppName] = useState(false);
  const [loading, setLoading] = useState(false);
  const history = useNavigate();
  function checkAppName() {
    if (validator.isEmpty(appName, { ignore_whitespace: true })) {
      setError("Name is required to create a new app");
      return false;
    }
    if (isValidName(appName, true)) {
      setError("App name should contain alphanumeric char only");
      return false;
    }
    if (validator.isNumeric(appName[0], { no_symbols: true })) {
      setError("App name should not start with a number");
      return false;
    }
    if (appName.length > 60) {
      setError("App name cannot be longer than 60 characters");
      return false;
    }
    return true;
  }
  function handleAppNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    setAppName(event.target.value.trim());
    setError("");
    setValidAppName(true);
  }
  function handleAppNamePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text");
    const input = event.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = input.value.slice(0, start) + pasted + input.value.slice(end);
    if (next.length <= 60) return;
    event.preventDefault();
    setAppName(next.slice(0, 60).trim());
    setError("App name cannot be longer than 60 characters");
    setValidAppName(false);
  }
  function handleCancel(event: any) {
    event.preventDefault();
    onCancel();
    return;
  }
  async function handleSaveChanges(event: any) {
    event.preventDefault();
    if (checkAppName()) {
      try {
        setLoading(true);
        const raw = (await callApi("/apps", {
          method: "POST",
          data: { app_name: appName },
        })) as Record<string, unknown>;
        setLoading(false);
        history(`/application/manage-apps/${raw.app_id}`);
      } catch (err) {
        setLoading(false);
        if (err instanceof ApiClientError && Object.keys(err.fieldErrors).length > 0) {
          const message =
            err.fieldErrors.app_name || Object.values(err.fieldErrors)[0];
          setError(message);
          return;
        }
        setError((err as Error).message);
      }
    }
    return;
  }
  const modalContent = (
    <div
      className={`d-flex align-items-center justify-content-center ${Styles.overlay}`}
    >
      <div className={`p-4 ${Styles.popUpContent} mx-5`}>
        <form onSubmit={handleSaveChanges}>
          <div className="form-group">
            <p className="title_semibold_text mb-3">App Name</p>
            <Input
              type="text"
              name="appName"
              id="manage-apps-create-app-name"
              maxLength={60}
              value={appName}
              autoComplete="off"
              placeholder="Your App Name"
              hasError={error}
              isValid={validAppName}
              onChange={handleAppNameChange}
              onPaste={handleAppNamePaste}
              onBlur={checkAppName}
              customInputClass="border-radius-10"
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </div>
          <div className="d-flex align-items-center justify-content-between py-2 gap-3">
            <div className="w-100">
              <Button
                size={MEDIUM}
                id="manage-apps-create-cancel"
                onClick={handleCancel}
                hoveredStyle={cancelButtonHoverStyle}
                style={cancelButtonStyle}
                type="button"
                className="w-100"
              >
                Cancel
              </Button>
            </div>
            <div className="w-100">
              <Button
                size={MEDIUM}
                isLoading={loading}
                id="manage-apps-create-submit"
                hoveredStyle={primaryButtonHoverStyle}
                style={primaryButtonStyle}
                onClick={handleSaveChanges}
                className="w-100"
                type="submit"
              >
                Create App
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return <PortalOverlay>{modalContent}</PortalOverlay>;
}

export default CreateAppPopUp;
